import { useEffect, useState } from 'react';
import {
    Store,
    Plus,
    Search,
    MapPin,
    Mail,
    Phone,
    Edit2,
    Trash2,
    X,
    Loader2,
    User
} from 'lucide-react';
import { storesAPI } from '../lib/api';
import { toast } from 'react-hot-toast';
import Layout from '../components/Layout';

interface StoreData {
    _id: string;
    name: string;
    organizationId: any;
    contactEmail: string;
    contactPhone: string;
    address: {
        fullAddress: string;
        city: string;
        state: string;
        pincode: string;
        location: {
            type: string;
            coordinates: number[];
        };
    };
    status: 'active' | 'inactive';
    entityAdminId?: any;
    createdBy?: any;
    createdAt: string;
}

export default function Stores() {
    const [stores, setStores] = useState<StoreData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStore, setEditingStore] = useState<StoreData | null>(null);


    const [formData, setFormData] = useState({
        name: '',
        organizationId: '',
        contactEmail: '',
        contactPhone: '',
        address: {
            fullAddress: '',
            city: '',
            state: '',
            pincode: '',
            contactName: '',
            contactPhone: '',
            type: 'work',
            location: { type: 'Point', coordinates: [0, 0] }
        }
    });

    const [selectedOrg, setSelectedOrg] = useState<any>(null);

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        const orgStr = localStorage.getItem('selectedOrg');
        if (userStr) {
            // const user = JSON.parse(userStr);
            // setCurrentUser(user);
        }
        if (orgStr) {
            setSelectedOrg(JSON.parse(orgStr));
        }
        fetchStores();
    }, []);

    const fetchStores = async () => {
        try {
            setLoading(true);
            const orgStr = localStorage.getItem('selectedOrg');
            const params: any = {};
            if (orgStr) {
                params.organizationId = JSON.parse(orgStr)._id;
            }
            const response = await storesAPI.getAll(params);
            setStores(response.data);
        } catch (error: any) {
            toast.error('Failed to fetch stores');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        const orgStr = localStorage.getItem('selectedOrg');
        const orgId = orgStr ? JSON.parse(orgStr)._id : '';
        setFormData({
            name: '',
            organizationId: orgId,
            contactEmail: '',
            contactPhone: '',
            address: {
                fullAddress: '',
                city: '',
                state: '',
                pincode: '',
                contactName: '',
                contactPhone: '',
                type: 'work',
                location: { type: 'Point', coordinates: [0, 0] }
            }
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const submissionData = {
                ...formData,
                address: {
                    ...formData.address,
                    contactName: formData.address.contactName || formData.name,
                    contactPhone: formData.address.contactPhone || formData.contactPhone,
                }
            };

            if (editingStore) {
                await storesAPI.update(editingStore._id, submissionData);
                toast.success('Store updated successfully');
            } else {
                await storesAPI.create(submissionData);
                toast.success('Store created successfully');
            }
            setIsModalOpen(false);
            resetForm();
            fetchStores();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to save store');
        }
    };

    const handleEdit = (store: StoreData) => {
        setEditingStore(store);
        setFormData({
            name: store.name,
            organizationId: typeof store.organizationId === 'object' ? store.organizationId?._id : store.organizationId || '',
            contactEmail: store.contactEmail,
            contactPhone: store.contactPhone,
            address: {
                fullAddress: store.address?.fullAddress || '',
                city: store.address?.city || '',
                state: store.address?.state || '',
                pincode: store.address?.pincode || '',
                contactName: store.name,
                contactPhone: store.contactPhone,
                type: 'work',
                location: store.address?.location || { type: 'Point', coordinates: [0, 0] }
            }
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this store?')) {
            try {
                await storesAPI.delete(id);
                toast.success('Store deleted successfully');
                fetchStores();
            } catch (error: any) {
                toast.error('Failed to delete store');
            }
        }
    };

    const filteredStores = stores.filter(store =>
        store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.contactEmail.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {selectedOrg ? `Stores: ${selectedOrg.name}` : 'Store Management'}
                        </h1>
                        <p className="text-gray-500">
                            {selectedOrg ? `Showing all stores for ${selectedOrg.name}` : 'Manage your branch locations and settings'}
                        </p>
                        {selectedOrg && (
                            <button
                                onClick={() => {
                                    localStorage.removeItem('selectedOrg');
                                    setSelectedOrg(null);
                                    fetchStores();
                                }}
                                className="text-sm text-green-600 hover:text-green-700 font-medium mt-1 flex items-center gap-1"
                            >
                                <X size={14} />
                                Clear Organization Filter
                            </button>
                        )}
                    </div>
                    <button
                        onClick={() => {
                            setEditingStore(null);
                            resetForm();
                            setIsModalOpen(true);
                        }}
                        className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <Plus size={20} />
                        Add Store
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100">
                        <div className="relative max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search stores..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="animate-spin text-green-600 mb-4" size={40} />
                            <p className="text-gray-500">Loading stores...</p>
                        </div>
                    ) : filteredStores.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                            {filteredStores.map((store) => (
                                <div key={store._id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow relative group">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="bg-green-100 p-3 rounded-lg text-green-600">
                                            <Store size={24} />
                                        </div>
                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => handleEdit(store)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(store._id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>

                                    <h3 className="text-lg font-bold text-gray-900 mb-3">{store.name}</h3>

                                    <div className="space-y-2 text-sm text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <Mail size={16} className="text-gray-400" />
                                            {store.contactEmail}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Phone size={16} className="text-gray-400" />
                                            {store.contactPhone}
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <MapPin size={16} className="text-gray-400 mt-1 flex-shrink-0" />
                                            <span>
                                                {store.address?.fullAddress}, {store.address?.city}, {store.address?.state} - {store.address?.pincode}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 pt-2">
                                            <User size={16} className="text-gray-400" />
                                            <span className="text-xs">Admin: {typeof store.entityAdminId === 'object' ? store.entityAdminId.fullName : 'Assigned'}</span>
                                        </div>
                                        {store.createdBy && (
                                            <div className="flex items-center gap-2">
                                                <User size={16} className="text-gray-400" />
                                                <span className="text-xs text-gray-400 italic">By: {typeof store.createdBy === 'object' ? store.createdBy.fullName : 'System'}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${store.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                            }`}>
                                            {store.status.toUpperCase()}
                                        </span>
                                        <span className="text-xs text-gray-400">
                                            Created {new Date(store.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <Store className="mx-auto text-gray-300 mb-4" size={64} />
                            <h3 className="text-lg font-medium text-gray-900">No stores found</h3>
                            <p className="text-gray-500 mb-6">Add your first store to start managing inventory and orders.</p>
                            <button
                                onClick={() => {
                                    setEditingStore(null);
                                    resetForm();
                                    setIsModalOpen(true);
                                }}
                                className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-all shadow-lg shadow-green-600/20 font-bold"
                            >
                                <Plus size={24} />
                                Create Your First Store
                            </button>
                        </div>
                    )}
                </div>

                {/* Modal */}
                {isModalOpen && (
                    <div className="modal-overlay backdrop-blur-sm">
                        <div className="modal-content max-w-2xl">
                            <div className="flex items-center justify-between p-6 border-b border-gray-100">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {editingStore ? 'Edit Store' : 'Add New Store'}
                                </h2>
                                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="col-span-2">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Store Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                            placeholder="e.g. Downtown Branch"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Contact Email</label>
                                        <input
                                            type="email"
                                            required
                                            value={formData.contactEmail}
                                            onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                            placeholder="branch@org.com"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Contact Phone</label>
                                        <input
                                            type="tel"
                                            required
                                            value={formData.contactPhone}
                                            onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                            placeholder="+91 98765 43210"
                                        />
                                    </div>

                                    <div className="col-span-2 space-y-4">
                                        <h3 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">Store Address</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="col-span-2">
                                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Full Address</label>
                                                <textarea
                                                    required
                                                    value={formData.address.fullAddress}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        address: { ...formData.address, fullAddress: e.target.value }
                                                    })}
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none h-20 resize-none transition-all"
                                                    placeholder="Street, Area, Landmark"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">City</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={formData.address.city}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        address: { ...formData.address, city: e.target.value }
                                                    })}
                                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                                    placeholder="City"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">State</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={formData.address.state}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        address: { ...formData.address, state: e.target.value }
                                                    })}
                                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                                    placeholder="State"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Pincode</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={formData.address.pincode}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        address: { ...formData.address, pincode: e.target.value }
                                                    })}
                                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                                    placeholder="6-digit Pincode"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                            <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-bold hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20"
                                >
                                    {editingStore ? 'Update Store' : 'Create Store'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
